// src/components/admin/pages/AboutPage.js
import React from 'react';
import { 
  Target, Eye, Shield, Users, Globe, Lock, CheckCircle2, Mail 
} from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="w-full">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-10 lg:p-12">
        {/* Header Sobrio */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4">
            <Globe className="w-8 h-8 text-slate-700" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight">
            Sobre Inkless
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Nuestra misión es democratizar el acceso a la firma digital segura, eliminando el papel y acelerando los procesos legales y comerciales en Latinoamérica.
          </p>
        </div>

        <div className="space-y-12 max-w-5xl mx-auto">
          {/* Misión y Visión */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-3">Nuestra Misión</h2>
              <p className="text-slate-600 leading-relaxed">
                Proporcionar una plataforma tecnológica accesible, segura y fácil de usar que permita a personas y empresas firmar documentos electrónicamente con plena validez legal, ahorrando tiempo y recursos.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                <Eye className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-3">Nuestra Visión</h2>
              <p className="text-slate-600 leading-relaxed">
                Ser la plataforma de referencia en gestión documental y firma electrónica en el mercado hispanohablante, reconocida por nuestra innovación, seguridad inquebrantable y compromiso con el usuario.
              </p>
            </div>
          </section>

          {/* Valores */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">Nuestros Valores</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { 
                  icon: Lock, 
                  title: 'Seguridad ante todo', 
                  desc: 'Protegemos cada documento con encriptación de nivel empresarial y hashes criptográficos.' 
                },
                { 
                  icon: Users, 
                  title: 'Centrados en el usuario', 
                  desc: 'Diseñamos cada función pensando en la simplicidad y la experiencia de quienes usan nuestra plataforma.' 
                },
                { 
                  icon: CheckCircle2, 
                  title: 'Transparencia total', 
                  desc: 'Creemos que la confianza se construye con claridad. No hay letras pequeñas ni sorpresas en nuestros servicios.' 
                }
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                  <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center mb-3">
                    <item.icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Compromiso con la Seguridad */}
          <section className="border-t border-slate-200 pt-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Lock className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">Compromiso con la Seguridad</h2>
            </div>
            <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-6 md:p-8">
              <p className="text-slate-700 leading-relaxed mb-4">
                En Inkless, la integridad de tus documentos es nuestra máxima prioridad. Utilizamos tecnología de punta para garantizar que cada firma tenga plena validez legal:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'Encriptación AES-256 para documentos en reposo',
                  'Protocolo TLS 1.3 para todas las comunicaciones',
                  'Hashes SHA-256 para verificación de integridad',
                  'Sellos de tiempo certificados (RFC 3161)',
                  'Cumplimiento con la Ley N° 27269 de Perú',
                  'Estándares internacionales eIDAS y ISO'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Contacto */}
          <section className="border-t border-slate-200 pt-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Mail className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">¿Quieres saber más?</h2>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 md:p-8">
              <p className="text-slate-600 mb-6">
                Si tienes preguntas sobre nuestra empresa, tecnología o alianzas estratégicas, nuestro equipo está listo para ayudarte.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Correo electrónico:</p>
                    <a href="mailto:contacto@visioner7.pe" className="text-blue-600 hover:underline text-sm">contacto@visioner7.pe</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Sitio web corporativo:</p>
                    <a href="https://visioner7.pe" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">visioner7.pe</a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-slate-200 text-center">
          <p className="text-slate-600">
            Gracias por confiar en nosotros para hacer tus procesos más seguros y eficientes.
          </p>
          <p className="text-sm text-slate-500 mt-2">
            El equipo de Inkless
          </p>
        </div>
      </div>
    </div>
  );
}